import React, { Dispatch, SetStateAction, useEffect, useMemo, useState } from 'react';

import { Territories } from '@amzn/coral_com-amazon-musicpapercontractconfigurationservice';
import alertErrorTokens from '@amzn/meridian-tokens/base/icon/alert';
import Box from '@amzn/meridian/box';
import Button from '@amzn/meridian/button';
import Column from '@amzn/meridian/column';
import Heading from '@amzn/meridian/heading';
import Icon from '@amzn/meridian/icon';
import Row from '@amzn/meridian/row';
import Text from '@amzn/meridian/text';
import Tooltip from '@amzn/meridian/tooltip';

import { CellClassRules, ColDef, GridApi } from 'ag-grid-community';
import { cloneDeep, isEmpty } from 'lodash';
import { BundlePricingSubAttribute } from 'src/constants';
import { GLOBAL_TERRITORY } from 'src/constants/context';
import { MISSING_REQUIRED_VALUES } from 'src/constants/errorMessages';
import { AppContext } from 'src/contexts/appContext';
import { useGridCopyPasteHandler } from 'src/hooks/useGridCopyPasteHandler';
import { IBundlePricingRowData } from 'src/types/IBundlePricing';
import { dropUndefined } from 'src/util/list-utils';

import { generateGridErrorId } from '../../AddPrimeAMFRateCard/rateCardValidationReducer';
import { BaseAgGrid } from '../../BaseAgGrid';
import { editBundlePricingColDefs } from '../../BundlePricing/bundlePricingColDefs';
import '../AddBundlePricing.scss';

export interface IEditBundlePricing {
    bundlePricingRowData: IBundlePricingRowData[];
    setBundlePricingRowData: Dispatch<SetStateAction<IBundlePricingRowData[]>>;
    paperPlanNames: string[];
    errors: Set<string>;
}

export const EditBundlePricing: React.FC<IEditBundlePricing> = ({
    bundlePricingRowData,
    setBundlePricingRowData,
    paperPlanNames,
    errors
}) => {
    const { metadata } = React.useContext(AppContext);
    const territories: Territories = useMemo(() => Object.values(metadata.territoryMapping), [metadata]);
    const currencies: string[] = useMemo(
        () =>
            dropUndefined(
                Array.from(new Set(territories.flatMap((t) => [t.accrualCurrency, t.settlementCurrency, t.currency])))
            ),
        [territories]
    );

    const errorCount = useMemo(
        () => new Set(Array.from(errors.values()).map((error) => error.split('-*-')[0])).size,
        [errors]
    );
    const {
        internalData: internalRowData,
        setInternalData: setInternalRowData,
        isPasting,
        onPasteStart,
        onPasteEnd,
        processDataFromClipboard
    } = useGridCopyPasteHandler(bundlePricingRowData);
    const [agGridApi, setAgGridApi] = useState<GridApi>();
    const cellClassRules: CellClassRules = {
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        'error-cell': (params) => errors.has(generateGridErrorId(Number(params.node.id), params.colDef.field!))
    };
    const columnDefs: ColDef[] = useMemo(() => {
        // Add checkbox
        const checkBoxColDef: ColDef[] = [
            {
                headerName: '',
                field: 'checkbox',
                cellClassRules,
                floatingFilter: false,
                checkboxSelection: true,
                headerCheckboxSelection: true,
                width: 50,
                sortable: false
            }
        ];

        return checkBoxColDef.concat(
            editBundlePricingColDefs.map((colDef) => {
                const baseDef = {
                    ...colDef,
                    cellClassRules,
                    editable: true
                };

                function useRichSelect(values: (string | undefined)[]) {
                    return {
                        ...baseDef,
                        cellClass: 'bundle-pricing-rich-select-cell',
                        cellEditor: 'agRichSelectCellEditor',
                        cellEditorPopup: true,
                        cellEditorParams: { values: dropUndefined(values) }
                    };
                }

                if (colDef.field === 'territory') {
                    return useRichSelect(
                        territories.filter((t) => t.territoryCode !== GLOBAL_TERRITORY.NAME).map((t) => t.territoryCode)
                    );
                } else if (colDef.field === 'planName') {
                    return useRichSelect(paperPlanNames);
                } else if (colDef.field === 'planSubAttribute') {
                    return useRichSelect(Object.values(BundlePricingSubAttribute));
                } else if (colDef.field === 'currency') {
                    return useRichSelect(currencies);
                }

                return baseDef;
            })
        );
    }, [cellClassRules]);

    // Sync internal row data with parent row data
    useEffect(() => {
        // Sync only after pasting is complete
        if (isPasting) {
            return;
        }

        setBundlePricingRowData(internalRowData);
    }, [internalRowData, isPasting]);

    const onCellValueChanged = () => {
        // Update row data to trigger sync
        setInternalRowData((prevState) => [...prevState]);
    };

    const handleAddRow = () => {
        const newRowData = cloneDeep(internalRowData);
        newRowData.push({} as IBundlePricingRowData);
        setInternalRowData(newRowData);
    };

    const handleDeleteSelectedRows = () => {
        const selectedRows = agGridApi?.getSelectedNodes();
        if (isEmpty(selectedRows)) {
            return;
        }

        const selectedRowsIdArr = selectedRows!.map((row) => row.id);
        const newRowData = internalRowData.filter((_, index) => !selectedRowsIdArr.includes(index.toString()));
        setInternalRowData(newRowData);
    };

    return (
        <div className="bundle-pricing-container">
            <Column className="bundle-pricing-content">
                <Heading level={2}>Edit bundle pricing</Heading>
                <Column spacing="300" width="100%" height="100%" minHeight={480}>
                    <Row alignmentHorizontal="justify">
                        <Row spacing="300">
                            <Button type="tertiary" size="small" onClick={handleAddRow}>
                                Add row
                            </Button>
                            <Button type="tertiary" size="small" onClick={handleDeleteSelectedRows}>
                                Delete selected row
                            </Button>
                        </Row>
                        {errorCount > 0 && (
                            <Tooltip position={'top'} title={MISSING_REQUIRED_VALUES}>
                                <Box type="fill" spacingInset="300 400" data-testid="error-count">
                                    <Row spacing="300">
                                        <Icon tokens={alertErrorTokens} />
                                        <Text type="h100">{errorCount}</Text>
                                        <Text>invalid</Text>
                                    </Row>
                                </Box>
                            </Tooltip>
                        )}
                    </Row>
                    <div className="bundle-pricing-grid">
                        <BaseAgGrid
                            columnDefs={columnDefs}
                            rowData={internalRowData}
                            setAgGridApi={setAgGridApi}
                            onPasteStart={onPasteStart}
                            onPasteEnd={onPasteEnd}
                            processDataFromClipboard={processDataFromClipboard}
                            onCellValueChanged={onCellValueChanged}
                            suppressRowClickSelection={true}
                            enableRangeSelection={true}
                            singleClickEdit={false}
                        />
                    </div>
                </Column>
            </Column>
        </div>
    );
};
